/**
 * /approvals — pending simulations list. Polls /api/simulations/pending
 * every 30s. Each card shows summary, kind, project_end change, affected
 * tasks count, new overloads, expiry countdown. Apply/Reject open a
 * confirm dialog with an optional reason; "Open Gantt" links to the
 * diff-Gantt page (filled in Stage B). 409 → toast about staleness, 410
 * → toast about expiry.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { Check, ExternalLink, X } from "lucide-react";
import { AppShell } from "@/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Surface } from "@/components/ui/surface";
import { ApiErrorNotice } from "@/components/ui/api-error-notice";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { showApiErrorToast } from "@/lib/api-error-toast";
import {
  usePendingApprovals,
  useCommitSimulation,
  useRejectSimulation,
} from "@/hooks/useApprovals";
import { useProjects } from "@/hooks/useProjects";
import { useUnits } from "@/hooks/useUnits";
import type { ApprovalRecord } from "@/api/approvals";
import type { components } from "@/api/types";
import { formatDateTime } from "@/lib/datetime";
import { formatAutoSummary } from "@/lib/approvals-summary";
import { useReleaseDirtyState } from "@/lib/releaseDirtyState";

type DecisionAction = "apply" | "reject";

interface DecisionDialogState {
  action: DecisionAction;
  record: ApprovalRecord;
}

export function ApprovalsPage(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError, error, refetch } = usePendingApprovals();
  const { data: projects } = useProjects({ limit: 200 });
  const projectNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of projects ?? []) {
      if (typeof p.id === "number") m.set(p.id, p.name);
    }
    return m;
  }, [projects]);
  const [dialog, setDialog] = useState<DecisionDialogState | null>(null);
  const [reason, setReason] = useState("");
  const [now, setNow] = useState(() => Date.now());
  useReleaseDirtyState(dialog !== null && reason !== "");

  const commitMut = useCommitSimulation();
  const rejectMut = useRejectSimulation();

  // Re-tick countdown labels every minute (cheap; doesn't refetch).
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const records = useMemo(
    () =>
      (data ?? [])
        .slice()
        .sort((a, b) => new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime()),
    [data],
  );

  function handleApiError(err: unknown, defaultMsg: string): void {
    showApiErrorToast(err, {
      fallbackMessage: defaultMsg,
      domainRules: [
        {
          code: "simulation-expired",
          status: 410,
          detail: "simulation expired",
          message: t("approvals.toast.expired"),
        },
      ],
    });
  }

  async function submitDecision(): Promise<void> {
    if (!dialog) return;
    const { action, record } = dialog;
    if (record.simulation_id == null) return;
    const simulationId = record.simulation_id;
    try {
      if (action === "apply") {
        const audit = await commitMut.mutateAsync({
          simulationId,
          reason: reason.trim(),
        });
        const affected = audit.diff?.affected_tasks?.length ?? 0;
        toast.success(t("approvals.toast.applied", { count: affected }));
      } else {
        await rejectMut.mutateAsync({
          simulationId,
          reason: reason.trim(),
        });
        toast.success(t("approvals.toast.rejected"));
      }
      setDialog(null);
      setReason("");
    } catch (err) {
      const fallback =
        action === "apply" ? t("approvals.toast.applyFailed") : t("approvals.toast.rejectFailed");
      handleApiError(err, fallback);
    }
  }

  function openDialog(action: DecisionAction, record: ApprovalRecord): void {
    setDialog({ action, record });
    setReason("");
  }

  return (
    <AppShell>
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">{t("approvals.title")}</h1>
        <span className="text-muted-foreground text-sm" data-testid="approvals-count">
          {t("approvals.count", { count: records.length })}
        </span>
      </div>

      <div className="mt-6 flex flex-col gap-3" data-testid="approvals-list">
        {isLoading ? (
          <div className="text-muted-foreground py-12 text-center">{t("common.loading")}</div>
        ) : isError ? (
          <div className="py-12 text-center">
            <ApiErrorNotice
              error={error}
              fallbackMessage={t("common.apiError.fallback")}
              className="text-destructive"
            />
            <Button variant="ghost" size="sm" className="ml-2" onClick={() => void refetch()}>
              {t("common.retry")}
            </Button>
          </div>
        ) : records.length === 0 ? (
          <div className="text-muted-foreground py-12 text-center" data-testid="approvals-empty">
            {t("approvals.empty")}
          </div>
        ) : (
          records.map((rec) => (
            <ApprovalCard
              key={rec.simulation_id}
              record={rec}
              now={now}
              locale={i18n.resolvedLanguage}
              projectName={
                typeof rec.project_id === "number"
                  ? (projectNameById.get(rec.project_id) ?? null)
                  : null
              }
              onApply={() => openDialog("apply", rec)}
              onReject={() => openDialog("reject", rec)}
            />
          ))
        )}
      </div>

      <Dialog
        open={dialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDialog(null);
            setReason("");
          }
        }}
      >
        <DialogContent data-testid="approvals-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialog?.action === "apply"
                ? t("approvals.dialog.applyTitle")
                : t("approvals.dialog.rejectTitle")}
            </DialogTitle>
            <DialogDescription>
              {dialog?.record ? formatAutoSummary(dialog.record, t) : ""}
            </DialogDescription>
          </DialogHeader>
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">{t("approvals.dialog.reasonLabel")}</span>
            <Input
              data-testid="approvals-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("approvals.dialog.reasonPlaceholder")}
            />
          </label>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDialog(null);
                setReason("");
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              data-testid="approvals-decision-submit"
              variant={dialog?.action === "reject" ? "destructive" : "default"}
              disabled={commitMut.isPending || rejectMut.isPending}
              onClick={() => void submitDecision()}
            >
              {dialog?.action === "apply"
                ? t("approvals.dialog.applyConfirm")
                : t("approvals.dialog.rejectConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

interface ApprovalCardProps {
  record: ApprovalRecord;
  now: number;
  locale?: string;
  projectName: string | null;
  onApply: () => void;
  onReject: () => void;
}

function ApprovalCard({
  record,
  now,
  locale,
  projectName,
  onApply,
  onReject,
}: ApprovalCardProps): React.ReactElement {
  const { t } = useTranslation();
  const unitsQuery = useUnits();
  const unitNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const u of unitsQuery.data ?? []) if (u.id != null) map.set(u.id, u.name);
    return map;
  }, [unitsQuery.data]);
  const overloads = record.diff.new_overloads ?? [];
  const affected = record.diff.affected_tasks?.length ?? 0;
  const delta = record.diff.project_end_change_days;
  const expiresAt = new Date(record.expires_at).getTime();
  const remaining = Math.max(0, expiresAt - now);
  const expired = remaining === 0;

  return (
    <Surface
      className="flex flex-col gap-3 p-4"
      data-testid={`approval-card-${record.simulation_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-base font-medium">{formatAutoSummary(record, t)}</span>
          <span className="text-muted-foreground text-sm">
            {`${t("approvals.kindLabel")}: ${t(`approvals.kind.${record.kind}`)}`}
            {record.project_id !== null && record.project_id !== undefined ? (
              <>
                {" · "}
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: String(record.project_id) }}
                  className="text-primary hover:underline"
                  data-testid="approval-project-link"
                >
                  {projectName ?? t("approvals.project", { id: record.project_id })}
                </Link>
              </>
            ) : null}
            {record.user_comment.trim() !== "" ? (
              <>
                {" · "}
                {`${t("approvals.userCommentLabel")}: `}
                <span data-testid="approval-user-comment">«{record.user_comment}»</span>
              </>
            ) : null}
          </span>
        </div>
        <div className="flex flex-col items-end gap-1 text-sm">
          <span
            className={
              expired
                ? "text-destructive font-medium"
                : remaining < 60 * 60 * 1000
                  ? "text-[var(--tone-caution-fg)]"
                  : "text-muted-foreground"
            }
            data-testid="approval-ttl"
          >
            {expired
              ? t("approvals.expired")
              : t("approvals.expiresIn", { value: formatRemaining(remaining, t) })}
          </span>
          <span className="text-muted-foreground">{formatDateTime(record.expires_at, locale)}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 text-sm">
        <Stat
          label={t("approvals.projectEndChange")}
          value={delta === 0 ? "0" : delta > 0 ? `+${delta}` : String(delta)}
          tone={delta > 0 ? "danger" : delta < 0 ? "success" : "neutral"}
          testId="approval-project-end"
        />
        <Stat
          label={t("approvals.affectedTasks")}
          value={String(affected)}
          tone="neutral"
          testId="approval-affected"
        />
        <Stat
          label={t("approvals.overloads")}
          value={String(overloads.length)}
          tone={overloads.length > 0 ? "danger" : "neutral"}
          testId="approval-overloads"
        />
      </div>

      {overloads.length > 0 && (
        <ul
          className="border-border flex flex-col gap-1 rounded-sm border-l-2 border-destructive/40 pl-3 text-sm"
          data-testid="approval-overload-list"
        >
          {overloads.slice(0, 5).map((o, idx) => (
            <li key={`${o.unit_id}-${o.period_start}-${idx}`}>
              <OverloadLine
                event={o}
                locale={locale}
                unitName={unitNameById.get(o.unit_id) ?? String(o.unit_id)}
              />
            </li>
          ))}
          {overloads.length > 5 && (
            <li className="text-muted-foreground">
              {t("approvals.overloadsMore", { count: overloads.length - 5 })}
            </li>
          )}
        </ul>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button asChild variant="outline" size="sm" data-testid="approval-open-gantt">
          <Link to="/approvals/$simulationId" params={{ simulationId: record.simulation_id ?? 0 }}>
            <ExternalLink className="h-4 w-4" />
            {t("approvals.openGantt")}
          </Link>
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onReject}
          data-testid="approval-reject"
          disabled={expired}
        >
          <X className="h-4 w-4" />
          {t("approvals.reject")}
        </Button>
        <Button size="sm" onClick={onApply} data-testid="approval-apply" disabled={expired}>
          <Check className="h-4 w-4" />
          {t("approvals.apply")}
        </Button>
      </div>
    </Surface>
  );
}

interface StatProps {
  label: string;
  value: string;
  tone: "success" | "danger" | "neutral";
  testId: string;
}

function Stat({ label, value, tone, testId }: StatProps): React.ReactElement {
  const color =
    tone === "danger"
      ? "text-destructive"
      : tone === "success"
        ? "text-[var(--tone-success-fg)]"
        : "text-foreground";
  return (
    <div className="flex flex-col">
      <span className="text-muted-foreground text-sm">{label}</span>
      <span className={`font-mono text-sm font-medium ${color}`} data-testid={testId}>
        {value}
      </span>
    </div>
  );
}

interface OverloadLineProps {
  event: components["schemas"]["OverloadEvent"];
  locale?: string;
  unitName: string;
}

function OverloadLine({ event, locale, unitName }: OverloadLineProps): React.ReactElement {
  const ratio = Math.round(event.overload_ratio * 100);
  const range =
    event.period_start === event.period_end
      ? formatShortDate(event.period_start, locale)
      : `${formatShortDate(event.period_start, locale)} – ${formatShortDate(event.period_end, locale)}`;
  return (
    <span>
      <span className="font-medium">{unitName}</span>
      {" · "}
      {range}
      {" · "}
      <span className="text-destructive">{`${ratio}%`}</span>
    </span>
  );
}

function formatShortDate(iso: string, locale?: string): string {
  // Avoid a full date-fns import path here; locale only chooses ru/en order.
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(locale === "en" ? "en-GB" : "ru-RU", {
    day: "2-digit",
    month: "2-digit",
  });
}

function formatRemaining(ms: number, t: TFunction): string {
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours >= 1) {
    return t("approvals.timeHM", { h: hours, m: minutes });
  }
  return t("approvals.timeM", { m: minutes });
}
